import React from "react";
import {
	Box,
	Button,
	Chip,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import StarTwoToneIcon from "@mui/icons-material/StarTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import dayjs from "dayjs";
import type { IFeedbackItem } from "src/Types/module/feedback/feedback.types";

interface FeedbackTableRowProps {
	feedback: IFeedbackItem;
	sl: number;
	onView: (feedback: IFeedbackItem) => void;
}

const FeedbackTableRow: React.FC<FeedbackTableRowProps> = ({
	feedback,
	sl,
	onView,
}) => {
	const getUserTypeColor = (type: string): "primary" | "secondary" | "info" | "default" => {
		const lower = (type || "").toLowerCase();
		if (lower === "visitor") return "info";
		if (lower === "exhibitor") return "secondary";
		if (lower === "b2b") return "primary";
		return "default";
	};

	const truncate = (str?: string | null, maxLen = 65) => {
		if (!str) return "—";
		if (str.length <= maxLen) return str;
		return `${str.substring(0, maxLen)}...`;
	};

	return (
		<TableRow hover key={feedback.id}>
			{/* SL */}
			<TableCell>
				<span>{sl}</span>
			</TableCell>

			{/* User Type */}
			<TableCell>
				<Chip
					label={feedback.user_type?.toUpperCase() || "N/A"}
					size="small"
					color={getUserTypeColor(feedback.user_type)}
					sx={{ fontWeight: 600, fontSize: "0.72rem", height: 22 }}
				/>
			</TableCell>

			{/* Name & Organization */}
			<TableCell>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
					<Typography variant="body2" fontWeight={600} color="text.primary">
						<span>{feedback.name || "N/A"}</span>
					</Typography>
					{feedback.organization && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontSize: "0.75rem" }}
						>
							<span>{feedback.organization}</span>
						</Typography>
					)}
				</Box>
			</TableCell>

			{/* Contact (Email & Phone) */}
			<TableCell>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
					<Typography variant="body2" fontWeight={500}>
						<span>{feedback.user_email || "N/A"}</span>
					</Typography>
					{feedback.phone && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontSize: "0.75rem" }}
						>
							<span>{feedback.phone}</span>
						</Typography>
					)}
				</Box>
			</TableCell>

			{/* Rating */}
			<TableCell>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
					<StarTwoToneIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
					<Typography variant="body2" fontWeight={700}>
						<span>{feedback.rating}/5</span>
					</Typography>
				</Box>
			</TableCell>

			{/* App Feedback Preview */}
			<TableCell sx={{ maxWidth: 280 }}>
				<Tooltip title={feedback.app_feedback || ""} arrow placement="top">
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							fontSize: "0.825rem",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							cursor: "pointer",
						}}
						onClick={() => onView(feedback)}
					>
						<span>{truncate(feedback.app_feedback)}</span>
					</Typography>
				</Tooltip>
			</TableCell>

			{/* Date */}
			<TableCell>
				<Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
					<span>
						{feedback.created_at
							? dayjs(feedback.created_at).format("DD-MMM-YYYY hh:mm A")
							: "—"}
					</span>
				</Typography>
			</TableCell>

		</TableRow>
	);
};

export default FeedbackTableRow;
